// src/components/quickMessage/Banner.tsx -- single Quick Message banner.
//
// Two visual states: closed (single tap target with snippet) and expanded
// (full body + reply input + Dismiss / Send reply). Driven by props -- the
// HomeSection orchestrator owns the expansion state so the multi-pill flow
// can render a banner inline beneath the name chips.
//
// Mobile keyboard does NOT auto-pop on expand (gotcha #13). The textarea is
// a regular input the user must tap to focus. The Send button is disabled
// when the trimmed reply is empty.
//
// The deleted-sender path (orphan FK) renders a muted avatar + "Former
// colleague" + disabled reply input + a caption explaining why -- per
// gotcha #16. The reply form is suppressed entirely (no point typing a
// reply that the API would reject anyway).

import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";
import { C, F } from "../../theme";
import {
  bannerMetaLine,
  QM,
  senderFullName,
  senderShortName,
  timeSinceLabel,
  type QuickMessage,
  type ThreadMessage,
} from "../../lib/quickMessages";
import { QmAvatar } from "./QmAvatar";
import { ThreadStrip } from "./ThreadStrip";

export interface BannerProps {
  message: QuickMessage;
  expanded: boolean;
  /** Bumped by the parent every 30s to force time-since recompute. */
  tick: number;
  thread: ThreadMessage[] | null;
  threadOpen: boolean;
  threadLoading: boolean;
  threadError: string | null;
  /** True while the underlying inbox is locked by an in-flight write. */
  busy: boolean;
  onExpand: () => void;
  onCollapse: () => void;
  onToggleThread: () => void;
  onDismiss: () => void;
  onSendReply: (body: string) => void;
}

const BANNER_KEYFRAMES = `
@keyframes qm-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(61,111,168,0.55); }
  70%  { box-shadow: 0 0 0 10px rgba(61,111,168,0); }
  100% { box-shadow: 0 0 0 0 rgba(61,111,168,0); }
}
`;

export function Banner(props: BannerProps) {
  return (
    <>
      <div style={cardStyle}>
        <span style={leftRuleStyle} aria-hidden="true" />
        {props.expanded ? <ExpandedBody {...props} /> : <ClosedBody {...props} />}
      </div>
      <style>{BANNER_KEYFRAMES}</style>
    </>
  );
}

// ---------------------------------------------------------------------------
// Closed body
// ---------------------------------------------------------------------------

function ClosedBody({ message, tick, onExpand }: BannerProps) {
  // The tick prop is referenced so React re-renders on the 30s pulse and the
  // label recomputes. We don't read it directly -- timeSinceLabel uses the
  // current time, which advances naturally.
  void tick;
  const isUnread = message.readAt === null;
  const since = timeSinceLabel(message.createdAt);
  const fullName = senderFullName(message.sender);
  const aria = `Quick message from ${fullName}, ${since.toLowerCase()}. Tap to reply.`;

  return (
    <button
      type="button"
      onClick={onExpand}
      aria-label={aria}
      style={closedBtnStyle}
    >
      <QmAvatar sender={message.sender} size={42} />
      <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
        <div style={eyebrowStyle}>
          {isUnread && <PulseDot />}
          Quick message · {since.toLowerCase()}
        </div>
        <div style={fromNameStyle}>{fullName}</div>
        <div style={snippetStyle}>{message.body}</div>
      </div>
      <span aria-hidden="true" style={chevronStyle}>
        ›
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Expanded body
// ---------------------------------------------------------------------------

function ExpandedBody({
  message,
  tick,
  thread,
  threadOpen,
  threadLoading,
  threadError,
  busy,
  onCollapse,
  onToggleThread,
  onDismiss,
  onSendReply,
}: BannerProps) {
  void tick;
  const [reply, setReply] = useState("");
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Esc to collapse, unless focus is inside the textarea (then it just blurs).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (document.activeElement === inputRef.current) {
        inputRef.current?.blur();
        return;
      }
      onCollapse();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCollapse]);

  const trimmedReply = reply.trim();
  const sendDisabled =
    busy ||
    trimmedReply.length === 0 ||
    trimmedReply.length > 100 ||
    message.sender.deleted;
  const orphan = message.sender.deleted;

  function handleSend() {
    if (sendDisabled) return;
    onSendReply(trimmedReply);
    setReply("");
  }

  return (
    <div style={openBodyStyle}>
      <div style={openHeaderStyle}>
        <QmAvatar sender={message.sender} size={38} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={openFromStyle}>{senderFullName(message.sender)}</div>
          <div style={openTimeStyle}>{bannerMetaLine(message)}</div>
        </div>
        {message.threadCount > 0 && !orphan && (
          <button
            type="button"
            onClick={onToggleThread}
            aria-label={threadOpen ? "Hide earlier messages" : "Show earlier messages"}
            aria-expanded={threadOpen}
            style={historyBtnStyle(threadOpen)}
          >
            <ClockIcon active={threadOpen} />
            <span style={historyCountStyle}>
              {message.threadCount > 9 ? "9+" : message.threadCount}
            </span>
          </button>
        )}
      </div>

      {threadOpen && (
        <ThreadStrip
          messages={thread}
          loading={threadLoading}
          error={threadError}
          otherPartyName={senderShortName(message.sender)}
        />
      )}

      <div style={bodyTextStyle}>{message.body}</div>

      {orphan ? (
        <div style={orphanNoticeStyle}>
          Can&rsquo;t reply &mdash; this person has left.
        </div>
      ) : (
        <>
          <textarea
            ref={inputRef}
            value={reply}
            maxLength={100}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Type a reply…"
            style={replyInputStyle}
            disabled={busy}
            aria-label="Reply"
          />
          <div style={counterStyle} aria-live="polite">
            <span style={{ color: reply.length >= 90 ? C.brassDk : C.inkMute }}>
              {reply.length}
            </span>{" "}
            / 100
          </div>
          <div style={actionsRowStyle}>
            <button
              type="button"
              onClick={onDismiss}
              disabled={busy}
              style={ghostBtnStyle(busy)}
            >
              Dismiss
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sendDisabled}
              style={primaryBtnStyle(sendDisabled)}
            >
              <PlaneIcon /> Send reply
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-icons
// ---------------------------------------------------------------------------

function PulseDot() {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        background: QM.base,
        animation: "qm-pulse 1.6s ease-out infinite",
        display: "inline-block",
        flexShrink: 0,
      }}
    />
  );
}

function ClockIcon({ active }: { active: boolean }) {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke={active ? QM.dark : C.inkMute}
      strokeWidth={active ? 2 : 1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function PlaneIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path d="M22 2 11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const cardStyle: CSSProperties = {
  background: C.card,
  border: `1px solid ${QM.light}`,
  borderRadius: 16,
  boxShadow:
    "0 1px 0 rgba(61,111,168,0.06), 0 4px 12px rgba(61,111,168,0.08)",
  overflow: "hidden",
  position: "relative",
};

const leftRuleStyle: CSSProperties = {
  position: "absolute",
  left: 0,
  top: 0,
  bottom: 0,
  width: 3,
  background: `linear-gradient(180deg, ${QM.base}, ${QM.dark})`,
};

const closedBtnStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 14px 12px 16px",
  width: "100%",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  textAlign: "left",
  fontFamily: F.body,
  WebkitTapHighlightColor: "transparent",
};

const eyebrowStyle: CSSProperties = {
  fontFamily: F.body,
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: QM.dark,
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginBottom: 2,
};

const fromNameStyle: CSSProperties = {
  fontFamily: F.serif,
  fontSize: 15,
  fontWeight: 700,
  color: C.ink,
  letterSpacing: -0.2,
  lineHeight: 1.15,
};

const snippetStyle: CSSProperties = {
  fontFamily: F.serif,
  fontStyle: "italic",
  fontSize: 13.5,
  color: C.inkSoft,
  lineHeight: 1.4,
  marginTop: 3,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const chevronStyle: CSSProperties = {
  color: QM.dark,
  fontSize: 18,
  fontWeight: 700,
  flexShrink: 0,
};

const openBodyStyle: CSSProperties = {
  padding: "16px 16px 14px 18px",
};

const openHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginBottom: 12,
};

const openFromStyle: CSSProperties = {
  fontFamily: F.serif,
  fontSize: 15,
  fontWeight: 700,
  color: C.ink,
  letterSpacing: -0.2,
};

const openTimeStyle: CSSProperties = {
  fontFamily: F.mono,
  fontSize: 10.5,
  color: C.inkMute,
  letterSpacing: 0.6,
  marginTop: 1,
};

function historyBtnStyle(active: boolean): CSSProperties {
  return {
    position: "relative",
    width: 30,
    height: 30,
    borderRadius: 10,
    background: active ? QM.bg : C.paperSoft,
    border: `1px solid ${active ? QM.light : C.hairline}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    padding: 0,
  };
}

const historyCountStyle: CSSProperties = {
  position: "absolute",
  top: -4,
  right: -4,
  background: QM.base,
  color: "white",
  fontFamily: F.body,
  fontSize: 9,
  fontWeight: 800,
  width: 14,
  height: 14,
  borderRadius: 7,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: `1.5px solid ${C.card}`,
  letterSpacing: 0,
};

const bodyTextStyle: CSSProperties = {
  fontFamily: F.serif,
  fontSize: 17,
  fontWeight: 500,
  color: C.ink,
  lineHeight: 1.45,
  margin: "4px 0 14px",
  letterSpacing: -0.1,
};

const replyInputStyle: CSSProperties = {
  width: "100%",
  minHeight: 72,
  padding: "12px 14px",
  background: C.paperSoft,
  border: `1px solid ${C.hairline}`,
  borderRadius: 12,
  fontFamily: F.body,
  fontSize: 14.5,
  color: C.ink,
  fontWeight: 500,
  lineHeight: 1.45,
  resize: "none",
  outline: "none",
  boxSizing: "border-box",
};

const counterStyle: CSSProperties = {
  fontFamily: F.mono,
  fontSize: 11,
  fontWeight: 700,
  color: C.inkMute,
  letterSpacing: 0.6,
  marginTop: 8,
  textAlign: "right",
};

const actionsRowStyle: CSSProperties = {
  display: "flex",
  gap: 10,
  marginTop: 12,
  alignItems: "center",
};

function ghostBtnStyle(disabled: boolean): CSSProperties {
  return {
    flex: 1,
    minHeight: 42,
    padding: "9px 14px",
    borderRadius: 12,
    fontFamily: F.body,
    fontWeight: 800,
    fontSize: 13.5,
    background: "transparent",
    color: C.inkSoft,
    border: `1px solid ${C.hairline}`,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    letterSpacing: 0.2,
  };
}

function primaryBtnStyle(disabled: boolean): CSSProperties {
  return {
    flex: 1,
    minHeight: 42,
    padding: "9px 14px",
    borderRadius: 12,
    fontFamily: F.body,
    fontWeight: 800,
    fontSize: 13.5,
    background: C.brassDk,
    color: C.brassGlow,
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    boxShadow:
      "inset 0 1px 0 rgba(244,226,168,0.3), 0 2px 6px rgba(122,84,11,0.15)",
    letterSpacing: 0.2,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  };
}

const orphanNoticeStyle: CSSProperties = {
  fontFamily: F.serif,
  fontStyle: "italic",
  fontSize: 13,
  color: C.inkSoft,
  background: C.paperWarm,
  padding: "10px 12px",
  borderRadius: 10,
  border: `1px dashed ${C.hairline}`,
  marginTop: 4,
};
